<script setup>
// Shared catalog page for /publisher/<slug>/<sub?> (brand attribute) and
// /license/<slug>/<sub?> (license attribute): CMS-driven hero (banner/logo/
// description from the Strapi cld-publisher / cld-license collections) over an
// attribute-filtered product listing from products/search/text.
//
// Virtual entity fallback: every brand/license that exists on products gets a
// page even without a CMS entry — the slug is matched against the search
// facets, and the page renders with its built-in fallbacks (collage banner,
// no logo/description). A CMS entry, when present, always wins and adds the
// editorial content. 404 only when the slug matches neither.
//
// Brand facet keys are display text ("Funko"); license facet keys are PIM
// codes ("harry_potter") — virtual license names are prettified from the code.
import { useDebounceFn } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { useUtilityStore } from "~/store/UtililityStore";
import productBox1 from "./product-box/product-box1.vue";

const props = defineProps({
  entity: { type: String, required: true }, // 'publisher' | 'license'
});

const entity = props.entity;
const attrKey = entity === "publisher" ? "brand" : "license";
const basePath = `/${entity}`;

const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();
const localePath = useLocalePath();

const slug = computed(() => String(route.params.slug ?? "").toLowerCase());
// The optional second URL segment is a category (chip slug, e.g.
// /publisher/nintendo/rpg). Without it the full catalog is shown.
const sub = computed(() => String(route.params.sub ?? "").toLowerCase());

// ---- CMS content (SSR, via the Nitro proxy which owns caching) ----
const { data: cmsEntity } = await useFetch(
  () => `/api/cms/cld-${entity}?slug=${encodeURIComponent(slug.value)}&locale=${locale.value}`,
  {
    key: `cms-${entity}-${slug.value}`,
    watch: [locale],
    default: () => null,
  },
);

// ---- Products: lean page-local fetch (deliberately NOT ProductStore — it
// shares global useState keys with the search page, which would clobber them
// here and wipe them on route-leave) ----
const utilityStore = useUtilityStore();
const { selectedLanguage } = storeToRefs(utilityStore);

const itemsPerPage = 30;

// Auth-aware search call, mirroring useFetchApi's transport: on the server the
// httpOnly authToken cookie is attached directly; on the client authenticated
// requests tunnel through /api/auth/proxy (the token never reaches the
// browser). $fetch (not useFetch/useFetchApi): these run again from
// useAsyncData watchers after mount, where Nuxt refuses a new useFetch
// instance.
const isAuthenticated = useCookie("isAuthenticated");
const serverAuthToken = import.meta.server ? useCookie("authToken").value || "" : "";

const searchFetch = async (body) => {
  const url = useURLs[NODE_ENV].PRODUCT_SEARCH_URL;
  const headers = {
    client_id: useURLs[NODE_ENV].CLIENT_ID,
    client_secret: useURLs[NODE_ENV].CLIENT_SECERET,
    "Content-Type": "application/json",
  };
  if (import.meta.client && isAuthenticated.value) {
    return await $fetch("/api/auth/proxy", {
      method: "POST",
      body: { url, method: "POST", body, headers },
    });
  }
  if (import.meta.server && serverAuthToken) {
    headers.Authorization = `Bearer ${serverAuthToken}`;
  }
  return await $fetch(url, { method: "POST", body, headers, retry: 1 });
};

const baseSearchBody = (overrides = {}) => ({
  prefix: "",
  range: 1,
  startRowIndex: 1,
  filters: { attribute: {}, categories: [], release: {} },
  selectedLanguage: selectedLanguage.value || "en_GB",
  sortOrder: "",
  searchByMultiIds: { active: false, ids: [] },
  ...overrides,
});

// ---- Virtual entity fallback ----
const { data: virtualEntity } = await useAsyncData(
  `${entity}-virtual-${slug.value}`,
  async () => {
    if (cmsEntity.value) return null;
    try {
      const data = await searchFetch(baseSearchBody());
      const match = (data?.aggregations?.[attrKey]?.buckets || []).find(
        (b) => slugifyBrand(b.key) === slug.value,
      );
      if (!match) return null;
      const facetValue = String(match.key);
      return {
        slug: slug.value,
        name: entity === "publisher" ? facetValue : prettyLicenseName(facetValue),
        filterValue: facetValue,
        bannerUrl: "",
        logoUrl: "",
        cardImageUrl: "",
        description: "",
        cardDescription: "",
        highlightedIds: [],
      };
    } catch {
      return null;
    }
  },
  { default: () => null },
);

const pageEntity = computed(() => cmsEntity.value ?? virtualEntity.value);
// The CMS publisher routes return brandFilterValue, the license ones
// filterValue — virtual entities always use filterValue.
const filterValue = computed(
  () => pageEntity.value?.filterValue ?? pageEntity.value?.brandFilterValue ?? "",
);

if (!pageEntity.value) {
  throw createError({ statusCode: 404, statusMessage: `${entity} not found`, fatal: true });
}

// Description paragraphs: the CMS field is markdown (richtext) — render it
// minimally as plain paragraphs without pulling in a markdown dependency.
const descriptionParagraphs = computed(() =>
  String(pageEntity.value?.description ?? "")
    .split(/\n{2,}/)
    .map((p) => p.replace(/^#{1,6}\s+/gm, "").replace(/[*_`>]/g, "").trim())
    .filter(Boolean),
);

const getFirstQueryValue = (value) => {
  if (value == null) return undefined;
  const v = Array.isArray(value) ? value[0] : value;
  return v == null ? undefined : String(v);
};

const searchQuery = ref(getFirstQueryValue(route.query.q) || "");
const debouncedSearchQuery = ref(searchQuery.value);
const currentPage = ref(Math.max(1, Number(getFirstQueryValue(route.query.page)) || 1));

// Prevent URL re-writes while applying state from route query
const isApplyingRouteQuery = ref(false);

const fetchEntityProducts = async () => {
  const value = filterValue.value;
  if (!value) return { products: [], count: 0 };

  // Resolve the sub segment against the (already loaded) chips: a category
  // chip filters by category code, anything else falls back to the DLG-style
  // game_type attribute filter.
  const chip = sub.value ? (categoryChips.value || []).find((c) => c.slug === sub.value) : null;
  const categories = chip?.kind === "category" ? chip.codes || [chip.code] : [];
  const gameType = sub.value && !categories.length ? { game_type: { [chip?.code || sub.value]: true } } : {};

  try {
    const data = await searchFetch(
      baseSearchBody({
        prefix: debouncedSearchQuery.value,
        range: itemsPerPage,
        startRowIndex: currentPage.value,
        // Newest first by default (BE sorts on Created desc); a keyword search
        // keeps the default relevance ranking instead.
        sortOrder: debouncedSearchQuery.value ? "" : "arrival_desc",
        filters: {
          attribute: {
            [attrKey]: { [value]: true },
            ...gameType,
          },
          categories,
          release: {},
        },
      }),
    );

    return {
      products: data?.products || [],
      count: data?.count || 0,
    };
  } catch {
    return { products: [], count: 0 };
  }
};

// ---- Highlighted products: CMS-curated IDs resolved via the search API's
// multi-id mode. Each ID is sent as both "DL45" and "45" variants (the PIM
// stores both forms), max 5 tokens per request. ----
const MAX_MULTI_IDS_PER_REQUEST = 5;

const normalizeMultiIds = (ids) => {
  const out = [];
  const seen = new Set();
  for (const raw of ids) {
    const token = String(raw ?? "").trim();
    if (!token) continue;
    const upper = token.toUpperCase();
    const variants = /^DL\d+$/i.test(upper)
      ? [upper, upper.replace(/^DL/i, "")]
      : /^\d+$/.test(upper)
        ? [upper, `DL${upper}`]
        : [token];
    for (const v of variants) {
      if (v && !seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    }
  }
  return out;
};

// Reactive keys encoding every fetch input: a changed key always fetches,
// sidestepping Nuxt's payload cache which short-circuits watch-triggered
// refetches of a same-key instance.
const highlightedKey = computed(() =>
  [`${entity}-highlighted`, slug.value, selectedLanguage.value, isAuthenticated.value ? 1 : 0].join("|"),
);

const { data: highlightedData } = await useAsyncData(
  highlightedKey,
  async () => {
    const ids = normalizeMultiIds(pageEntity.value?.highlightedIds ?? []);
    if (!ids.length) return [];

    const chunks = [];
    for (let i = 0; i < ids.length; i += MAX_MULTI_IDS_PER_REQUEST) {
      chunks.push(ids.slice(i, i + MAX_MULTI_IDS_PER_REQUEST));
    }

    const merged = [];
    const seen = new Set();
    for (const chunk of chunks) {
      try {
        const data = await searchFetch(
          baseSearchBody({
            range: Math.max(itemsPerPage, chunk.length),
            searchByMultiIds: { active: true, ids: chunk },
          }),
        );
        for (const p of data?.products || []) {
          const key = String(p?.Identifier ?? JSON.stringify(p));
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(p);
          }
        }
      } catch {
        // A failed chunk just means fewer highlighted products — not fatal.
      }
    }
    return merged;
  },
  {
    server: true,
    default: () => [],
  },
);
const highlightedProducts = computed(() => highlightedData.value ?? []);
// Only decorate the default view — hide the row while searching, paging, or
// inside a category.
const showHighlighted = computed(
  () =>
    highlightedProducts.value.length > 0 &&
    !debouncedSearchQuery.value &&
    currentPage.value === 1 &&
    !sub.value,
);

// ---- Future releases: preorder products for this entity releasing more than
// 2 weeks from now. The search API has no release-date-range filter, so the
// fetch narrows to preorders sorted by release date ascending and the date
// cutoff is applied to the returned page (preorder pools per entity are
// small). Rendered between the highlighted row and the full catalog.
const FUTURE_RELEASE_MIN_DAYS = 14;
const FUTURE_RELEASE_FETCH = 50;
const FUTURE_RELEASE_SHOW = 10;

const futureKey = computed(() =>
  [`${entity}-future`, slug.value, selectedLanguage.value, isAuthenticated.value ? 1 : 0].join("|"),
);

const { data: futureData } = await useAsyncData(
  futureKey,
  async () => {
    const value = filterValue.value;
    if (!value) return [];
    try {
      const data = await searchFetch(
        baseSearchBody({
          range: FUTURE_RELEASE_FETCH,
          sortOrder: "release_asc",
          filters: {
            attribute: {
              [attrKey]: { [value]: true },
              status_preorder: { "status preorder": true },
            },
            categories: [],
            release: {},
          },
        }),
      );
      const threshold = Date.now() + FUTURE_RELEASE_MIN_DAYS * 24 * 60 * 60 * 1000;
      return (data?.products || [])
        .filter((p) => {
          if (!p?.releaseDate) return false;
          const d = new Date(p.releaseDate).getTime();
          return Number.isFinite(d) && d > threshold;
        })
        .slice(0, FUTURE_RELEASE_SHOW);
    } catch {
      return [];
    }
  },
  {
    server: true,
    default: () => [],
  },
);
const futureProducts = computed(() => futureData.value ?? []);
// Same visibility rule as the highlighted row: default view only.
const showFuture = computed(
  () =>
    futureProducts.value.length > 0 &&
    !debouncedSearchQuery.value &&
    currentPage.value === 1 &&
    !sub.value,
);

// ---- Category chips from an entity-only aggregation query. Deliberately
// separate from the products fetch — buckets shift once a chip filter is
// applied, so chips stay stable only when computed from the unfiltered set.
//
// Two chip kinds share the row:
// - 'category': CLD catalog categories (Categories_agg child-level buckets,
//   codes like cld_242) — these cover every entity, merchandise included.
//   Labels come from /api/publisher/category-labels; the chip slug is the
//   slugified en_GB label so URLs stay stable across locales.
// - 'game_type': the DLG-style game-type facets, appended for gaming entities.
const prettyTypeLabel = (key) =>
  key.length <= 3 ? key.toUpperCase() : key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");

const categoriesKey = computed(() =>
  [`${entity}-categories`, slug.value, selectedLanguage.value].join("|"),
);

const { data: categoryChips } = await useAsyncData(
  categoriesKey,
  async () => {
    const value = filterValue.value;
    if (!value) return [];
    try {
      const [data, labelMap] = await Promise.all([
        searchFetch(
          baseSearchBody({
            filters: { attribute: { [attrKey]: { [value]: true } }, categories: [], release: {} },
          }),
        ),
        $fetch("/api/publisher/category-labels").catch(() => ({})),
      ]);

      const chips = [];
      const seenSlugs = new Set();

      // Category chips: flatten the child-level buckets (specific enough to be
      // useful — "Figures & Replica's", "PS5" — where parents are just
      // "Merchandising"/"Videogames"). The same child code can appear under
      // several parents; keep the max count. Fall back to the parent buckets
      // when an entity has no child-level categories.
      //
      // Each chip carries its full code SUBTREE (child + sub-child codes): the
      // search `Categories` field only holds the codes products are directly
      // assigned to (often the leaf level), so filtering by the child code
      // alone can match nothing — the sidebar filter does the same (it only
      // ever sends grandchild codes, see TreeNavigation.performFiltering).
      const parents = data?.aggregations?.Categories_agg?.buckets || [];
      const byCode = new Map();
      const addCode = (code, count, subtreeCodes) => {
        if (!code || !count) return;
        const entry = byCode.get(code) || { count: 0, codes: new Set([code]) };
        entry.count = Math.max(entry.count, count);
        for (const c of subtreeCodes) entry.codes.add(c);
        byCode.set(code, entry);
      };
      // The nested aggs are field-based cross-products (a doc in both PS4 and
      // PS5 leaks its PS4 leaf into the PS5 bucket's sub_child_agg), so only
      // codes whose REAL parent (from the label map) matches are kept.
      const isChildOf = (childCode, parentCode) =>
        labelMap?.[childCode]?.parent === String(parentCode);
      for (const p of parents) {
        for (const c of p?.child_agg?.buckets || []) {
          if (!c?.key) continue;
          const subs = (c?.sub_child_agg?.buckets || [])
            .map((s) => String(s.key))
            .filter((s) => s && isChildOf(s, c.key));
          addCode(String(c.key), c?.doc_count, subs);
        }
      }
      if (!byCode.size) {
        for (const p of parents) {
          if (!p?.key) continue;
          const subtree = [];
          for (const c of p?.child_agg?.buckets || []) {
            if (!c?.key || !isChildOf(String(c.key), p.key)) continue;
            subtree.push(String(c.key));
            for (const s of c?.sub_child_agg?.buckets || []) {
              if (s?.key && isChildOf(String(s.key), c.key)) subtree.push(String(s.key));
            }
          }
          addCode(String(p.key), p?.doc_count, subtree);
        }
      }
      const catChips = [...byCode.entries()]
        .map(([code, entry]) => {
          const labels = labelMap?.[code]?.labels;
          const label = labels?.[selectedLanguage.value] || labels?.en_GB || "";
          if (!label) return null;
          const chipSlug = slugifyBrand(labels?.en_GB || label);
          return chipSlug
            ? { kind: "category", code: String(code), codes: [...entry.codes], label, slug: chipSlug, count: entry.count }
            : null;
        })
        .filter(Boolean)
        .sort((a, b) => b.count - a.count);
      for (const c of catChips) {
        if (!seenSlugs.has(c.slug)) {
          seenSlugs.add(c.slug);
          chips.push(c);
        }
      }

      // game_type chips (DLG behavior), appended after the categories.
      for (const b of data?.aggregations?.game_type?.buckets || []) {
        if (!b.key || !b.doc_count) continue;
        const key = String(b.key);
        if (seenSlugs.has(key)) continue;
        seenSlugs.add(key);
        chips.push({ kind: "game_type", code: key, label: prettyTypeLabel(key), slug: key, count: b.doc_count });
      }

      return chips.slice(0, 12);
    } catch {
      return [];
    }
  },
  {
    server: true,
    default: () => [],
  },
);

// Label for the active sub segment (chips carry the localized label; fall
// back to prettifying the raw slug for deep links to unknown chips).
const subLabel = computed(() => {
  if (!sub.value) return "";
  const chip = (categoryChips.value || []).find((c) => c.slug === sub.value);
  return chip?.label || prettyTypeLabel(sub.value);
});

const categoryLink = (chipSlug) => ({
  path: localePath(chipSlug ? `${basePath}/${slug.value}/${chipSlug}` : `${basePath}/${slug.value}`),
  // Keep the search term across category switches; drop the page.
  query: debouncedSearchQuery.value ? { q: debouncedSearchQuery.value } : {},
});

// Entering/leaving a category restarts at page 1 without a URL rewrite fight.
watch(sub, () => {
  isApplyingRouteQuery.value = true;
  currentPage.value = 1;
  setTimeout(() => {
    isApplyingRouteQuery.value = false;
  }, 0);
});

const productsKey = computed(() =>
  [
    `${entity}-products`,
    slug.value,
    sub.value,
    debouncedSearchQuery.value,
    currentPage.value,
    selectedLanguage.value,
    isAuthenticated.value ? 1 : 0,
  ].join("|"),
);

const { data: productData, status } = await useAsyncData(
  productsKey,
  fetchEntityProducts,
  {
    server: true,
    default: () => ({ products: [], count: 0 }),
  },
);
const products = computed(() => productData.value?.products ?? []);
const productsCount = computed(() => productData.value?.count ?? 0);
const loading = computed(() => status.value === "pending");

// Banner fallback: when the CMS has no banner, tile small packshots from the
// current results across the banner area (same CDN transform as the product
// cards).
const { getImgUrl: buildImgUrl } = useProductImage();

// Random pick per visit, but seeded via useState so server and client shuffle
// identically (plain Math.random() in the computed would mismatch hydration).
const collageSeed = useState(`${entity}-collage-seed-${slug.value}`, () =>
  Math.floor(Math.random() * 2 ** 31),
);

const seededShuffle = (items, seed) => {
  const arr = [...items];
  let s = seed;
  for (let i = arr.length - 1; i > 0; i--) {
    // mulberry32-style step
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    const j = Math.floor(r * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// The collage is frozen on the first non-empty result set of the visit —
// recomputing it from the live results would reshuffle the banner on every
// search/page/category change. useState (not ref): category switches change
// the route path, which remounts the page component — a plain ref would reset
// and refill from the new category's products. Server and client fill it from
// the same hydrated payload, so SSR markup matches.
const collageSource = useState(`${entity}-collage-src-${slug.value}`, () => []);
watch(
  products,
  (list) => {
    if (collageSource.value.length || pageEntity.value?.bannerUrl) return;
    const seen = new Set();
    for (const p of list || []) {
      const img = Array.isArray(p?.images)
        ? p.images.find(
            // Skip the PIM "Image Coming Soon" placeholder — it must not tile
            // into the hero collage.
            (i) => typeof i === "string" && i.trim() && !/coming[_-]?soon/i.test(i),
          )
        : null;
      if (img) seen.add(img);
    }
    if (seen.size) collageSource.value = [...seen];
  },
  { immediate: true },
);

const bannerCollage = computed(() => {
  if (pageEntity.value?.bannerUrl) return [];
  return seededShuffle(collageSource.value, collageSeed.value)
    .slice(0, 6)
    .map((img) => buildImgUrl(img, { width: 320, height: 480 }));
});

// ---- URL state: q + page only ----
const setUrl = () => {
  if (isApplyingRouteQuery.value) return;
  const query = {};
  if (debouncedSearchQuery.value) query.q = debouncedSearchQuery.value;
  if (currentPage.value > 1) query.page = String(currentPage.value);
  isApplyingRouteQuery.value = true;
  router.replace({ query }).finally(() => {
    setTimeout(() => {
      isApplyingRouteQuery.value = false;
    }, 0);
  });
};

const updateDebouncedSearch = useDebounceFn((val) => {
  debouncedSearchQuery.value = val;
  currentPage.value = 1;
  setUrl();
}, 300);

const handleSearchSubmit = () => {
  if (isApplyingRouteQuery.value) return;
  debouncedSearchQuery.value = String(searchQuery.value || "");
  currentPage.value = 1;
  setUrl();
};

watch(searchQuery, (val) => {
  if (isApplyingRouteQuery.value) return;
  updateDebouncedSearch(val);
});

watch(currentPage, () => {
  if (isApplyingRouteQuery.value) return;
  setUrl();
  if (import.meta.client) window.scrollTo({ top: 0, behavior: "smooth" });
});

// Apply incoming q changes (e.g. header search navigations)
watch(() => route.query.q, (newQ) => {
  isApplyingRouteQuery.value = true;
  try {
    const next = getFirstQueryValue(newQ) || "";
    searchQuery.value = next;
    debouncedSearchQuery.value = next;
    currentPage.value = 1;
  } finally {
    isApplyingRouteQuery.value = false;
  }
});

const clearSearch = () => {
  searchQuery.value = "";
  debouncedSearchQuery.value = "";
  currentPage.value = 1;
  setUrl();
};

// ---- Pagination ----
const totalPages = computed(() => Math.ceil(productsCount.value / itemsPerPage));

const visiblePages = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push(-1);
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 2) pages.push(-1);
    pages.push(total);
  }

  return pages;
});

// ---- SEO ----
// hreflang alternates for every locale (+ x-default) are emitted globally by
// the layouts via useLocaleHead — only canonical/og/JSON-LD are page-level,
// matching the repo's convention (story.vue, faq).
const entityPath = computed(() =>
  sub.value ? `${basePath}/${slug.value}/${sub.value}` : `${basePath}/${slug.value}`,
);
const canonicalUrl = computed(() => `https://cld.eu${localePath(entityPath.value)}`);
const seoTitle = computed(
  () =>
    `${pageEntity.value?.name ?? ""}${sub.value ? ` ${subLabel.value}` : ""} — ${t(`${entity}.seo_title_suffix`)}`,
);
const seoDescription = computed(
  () => pageEntity.value?.cardDescription || t(`${entity}.seo_description`),
);
const seoImage = computed(
  () => pageEntity.value?.bannerUrl || pageEntity.value?.cardImageUrl || undefined,
);

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogUrl: () => canonicalUrl.value,
  ogType: "website",
  ogSiteName: "CLD Distribution",
  ogImage: () => seoImage.value,
  twitterCard: "summary_large_image",
  twitterTitle: () => seoTitle.value,
  twitterDescription: () => seoDescription.value,
  twitterImage: () => seoImage.value,
});

useHead({
  link: [{ rel: "canonical", href: () => canonicalUrl.value }],
  script: [
    {
      key: `${entity}-jsonld`,
      type: "application/ld+json",
      innerHTML: () =>
        JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: seoTitle.value,
          description: seoDescription.value,
          url: canonicalUrl.value,
          about: {
            // Publishers are companies; licenses are franchises/IPs.
            "@type": entity === "publisher" ? "Organization" : "Thing",
            name: pageEntity.value?.name ?? "",
            ...(pageEntity.value?.logoUrl ? { logo: pageEntity.value.logoUrl } : {}),
          },
        }),
    },
  ],
});
</script>

<template>
  <div v-if="pageEntity" class="min-h-screen bg-white">
    <!-- Hero banner: full-bleed image with logo, title, description overlaid -->
    <div class="relative h-72 md:h-[24rem] overflow-hidden border-b border-gray-100">
      <img
        v-if="pageEntity.bannerUrl"
        :src="pageEntity.bannerUrl"
        :alt="pageEntity.name"
        class="absolute inset-0 w-full h-full object-cover"
        decoding="async"
      />
      <div v-else-if="bannerCollage.length" class="absolute inset-0 flex">
        <img
          v-for="url in bannerCollage"
          :key="url"
          :src="url"
          alt=""
          aria-hidden="true"
          class="h-full flex-1 min-w-0 object-cover"
          decoding="async"
        />
      </div>
      <div
        v-else
        class="absolute inset-0"
        style="background: linear-gradient(135deg, #052e1a, #0f7a44)"
      ></div>
      <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>

      <!-- Overlay content -->
      <div class="absolute inset-x-0 bottom-0 pb-12 md:pb-14">
        <div class="max-w-7xl mx-auto px-4">
          <div class="flex items-center gap-4 mb-3">
            <img
              v-if="pageEntity.logoUrl"
              :src="pageEntity.logoUrl"
              :alt="`${pageEntity.name} logo`"
              class="h-16 w-16 md:h-20 md:w-20 rounded-xl bg-white/95 border border-gray-200 object-contain p-2 shadow-lg"
              decoding="async"
            />
            <h1 class="text-3xl sm:text-5xl font-bold tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
              <span class="relative inline-block">
                <span class="relative z-10">{{ pageEntity.name }}</span>
                <span
                  class="absolute left-0 -bottom-0.5 -z-0 h-2 w-full rounded-full bg-gradient-to-r from-[var(--color-primary)] via-[rgba(8,184,81,0.4)] to-transparent shadow-[0_0_16px_rgba(8,184,81,0.35)]"
                ></span>
              </span>
            </h1>
          </div>
          <div v-if="descriptionParagraphs.length" class="max-w-3xl space-y-1">
            <p
              v-for="(paragraph, i) in descriptionParagraphs"
              :key="i"
              class="text-sm text-gray-100 drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]"
            >
              {{ paragraph }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 pb-12">
      <!-- Search: floats over the banner's bottom edge, sticky while scrolling.
           The layout wrappers use no overflow-x-hidden (checked) — sticky works. -->
      <div class="sticky top-[80px] z-30 -mt-8 -mx-4 px-4 py-3 mb-6 flex justify-center">
        <form class="relative w-64 md:w-[32rem]" @submit.prevent="handleSearchSubmit">
          <input
            v-model="searchQuery"
            type="text"
            :aria-label="$t(`${entity}.search_placeholder`)"
            class="cld-input h-14 w-full !rounded-full pl-6 pr-16 bg-white border border-gray-300 shadow-[0_4px_24px_rgba(0,0,0,0.12)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
            :placeholder="$t(`${entity}.search_placeholder`)"
            name="search"
          />

          <div class="hidden absolute md:block top-1 right-1">
            <button
              type="submit"
              aria-label="Search"
              class="appearance-none rounded-full px-4 bg-[var(--color-primary)] text-white h-12 hover:bg-[#07a745] transition-colors"
            >
              <i class="fa fa-search" aria-hidden="true"></i>
            </button>
          </div>
        </form>
      </div>

      <!-- Category chips -->
      <div v-if="categoryChips.length" class="flex flex-wrap gap-2 mb-6">
        <NuxtLink
          :to="categoryLink()"
          class="px-4 py-1.5 rounded-full text-sm font-medium border transition-colors"
          :class="!sub
            ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
            : 'text-gray-700 border-gray-300 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]'"
        >
          {{ $t(`${entity}.categories_all`) }}
        </NuxtLink>
        <NuxtLink
          v-for="chip in categoryChips"
          :key="chip.slug"
          :to="categoryLink(chip.slug)"
          class="px-4 py-1.5 rounded-full text-sm font-medium border transition-colors"
          :class="sub === chip.slug
            ? 'bg-[var(--color-primary)] text-white border-[var(--color-primary)]'
            : 'text-gray-700 border-gray-300 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]'"
        >
          {{ chip.label }}
          <span class="opacity-60 ml-1">{{ chip.count }}</span>
        </NuxtLink>
      </div>

      <!-- Results count -->
      <div class="cld-card px-4 py-3 mb-6 flex items-center justify-end">
        <span class="text-sm text-gray-500">{{ $t(`${entity}.products_found`, { count: productsCount }) }}</span>
      </div>

      <!-- Highlighted products (CMS-curated), first row of the results area -->
      <div v-if="showHighlighted" class="mb-10">
        <h2 class="text-xl md:text-2xl font-semibold text-gray-900 mb-4">
          {{ $t(`${entity}.highlighted_title`) }}
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
          <div v-for="(product, index) in highlightedProducts" :key="'hl-' + product.Identifier" class="w-full my-2">
            <productBox1 :item="product" :index="index" />
          </div>
        </div>
      </div>

      <!-- Future releases: preorders releasing 2+ weeks out (default view only) -->
      <div v-if="showFuture" class="mb-10">
        <h2 class="text-xl md:text-2xl font-semibold text-gray-900 mb-4">
          {{ $t(`${entity}.future_releases_title`) }}
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
          <div v-for="(product, index) in futureProducts" :key="'fr-' + product.Identifier" class="w-full my-2">
            <productBox1 :item="product" :index="index" />
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary)]"></div>
      </div>

      <!-- Products Grid -->
      <div v-else-if="products.length > 0">
        <h2 class="text-xl md:text-2xl font-semibold text-gray-900 mb-4">
          {{ pageEntity.name }} — {{ $t(`${entity}.catalog_title`) }}
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
          <div v-for="(product, index) in products" :key="product.Identifier || index" class="w-full my-2">
            <productBox1 :item="product" :index="index" />
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12 cld-card">
        <p class="text-gray-500">{{ $t(`${entity}.empty_no_products`) }}</p>
        <button
          v-if="debouncedSearchQuery"
          class="mt-4 text-[var(--color-primary)] hover:underline"
          @click="clearSearch"
        >
          {{ $t(`${entity}.empty_clear_search`) }}
        </button>
      </div>

      <!-- Pagination -->
      <nav v-if="totalPages > 1" class="flex items-center justify-center gap-1 mt-8 mb-8">
        <button
          :disabled="currentPage === 1"
          class="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 hover:shadow-sm transition-all"
          @click="currentPage--"
        >
          &laquo;
        </button>

        <template v-for="(page, index) in visiblePages" :key="index">
          <span v-if="page === -1" class="px-2 text-gray-400">...</span>
          <button
            v-else
            class="w-10 h-10 rounded-lg text-sm font-medium transition-all"
            :class="currentPage === page
              ? 'bg-[var(--color-primary)] text-white'
              : 'hover:bg-gray-100 hover:shadow-sm text-gray-600'"
            @click="currentPage = page"
          >
            {{ page }}
          </button>
        </template>

        <button
          :disabled="currentPage === totalPages"
          class="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 hover:shadow-sm transition-all"
          @click="currentPage++"
        >
          &raquo;
        </button>
      </nav>
    </div>
  </div>
</template>
